'use client';

import { notFound } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { type GamesFilter, gamesFilterSelectBySEOTitle } from '@/api/games';
import GeneralSkeleton from '@/components/skeletons/GeneralSkeleton';
import { mapGameV2ToGameV1 } from '@/helpers/game';
import { useGameRedirect } from '@/hooks/profile';
import { usePlayerFieldsStatus } from '@/hooks/usePlayerFieldsStatus';
import { useResetPromoStore } from '@/hooks/useResetPromoStore';
import { isProviderOpenForCountry } from '@/modules/seo/metadata/providerLocaleRestrictions';
import { useGetCommonStore } from '@/store';
import { GameCard } from '..';

interface GameContainerParams {
  gameProvider: string;
  name: string;
}

export const GameContainer = ({ gameProvider, name }: GameContainerParams) => {
  const useCommonStore = useGetCommonStore();
  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const countryCode = useCommonStore.use.countryCode();

  const [gamePending, setGamePending] = useState(true);
  const [gameData, setGameData] = useState<GamesFilter.ResponseSelect>({});
  const inProcessRedirect = useGameRedirect(gameProvider);
  const { missingFields } = usePlayerFieldsStatus(gameProvider);

  useResetPromoStore();

  useEffect(() => {
    gamesFilterSelectBySEOTitle({
      gameSeoTitles: [name],
      device: isMobileDevice ? 'mobile' : 'desktop',
    })
      .then(({ data }) => {
        const isEmpty = data ? !Object.keys(data).length : true;
        if (isEmpty) {
          gamesFilterSelectBySEOTitle({
            gameSeoTitles: [`${name}-${gameProvider}`],
            device: isMobileDevice ? 'mobile' : 'desktop',
          })
            .then(({ data }) => {
              setGameData(data || {});
              setGamePending(false);
            })
            .catch(() => {
              setGamePending(false);
            });
          return;
        }
        setGameData(data || {});
        setGamePending(false);
      })
      .catch(() => {
        setGamePending(false);
      });
  }, [name, gameProvider, isMobileDevice]);

  const game = useMemo(
    () =>
      Object.values(gameData).find(({ provider }) => gameProvider === provider),
    [gameData, gameProvider]
  );

  if (gamePending || inProcessRedirect || missingFields.length) {
    return <GeneralSkeleton />;
  }

  const isGeoOpen = isProviderOpenForCountry(gameProvider, countryCode);

  if (!game && isGeoOpen) {
    return notFound();
  }

  return (
    <GameCard
      game={game ? mapGameV2ToGameV1([game])[0] : undefined}
      isGeoBlocked={!(isGeoOpen || game)}
    />
  );
};
